import React, { useEffect, useState } from "react";
import {
  Users,
  Package,
  MessageSquare,
  Plus,
  Trash2,
  RotateCcw,
  Save,
  Loader2,
  Check,
  X,
  GripVertical,
  Sparkles,
  ChevronRight,
  Lightbulb,
  Landmark,
  Filter,
  HardHat,
  Building2,
  FileStack,
  Tags,
  Zap,
  CalendarDays,
} from "lucide-react";
import AutomationsPanel from "../components/automations/AutomationsPanel";
import TabStatusesPanel from "../components/TabStatusesPanel";
import MessageTemplatesPanel from "../components/templates/MessageTemplatesPanel";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "sonner";
import SubcontractorsPanel from "../components/SubcontractorsPanel";
import TechniciensPanel from "../components/planning/TechniciensPanel";
import EntreprisePanel from "../components/EntreprisePanel";
import FamiliesPanel from "../components/families/FamiliesPanel";
import DossierPoseOrderPanel from "../components/DossierPoseOrderPanel";
import AppLayout, { PageHeader } from "../components/AppLayout";
import { api } from "../lib/api";

const TABS = [
  { id: "entreprise", label: "Entreprise & parrainage", icon: Building2 },
  { id: "familles", label: "Familles & annuaire", icon: Users },
  { id: "subcontractors", label: "Sous-traitants", icon: HardHat },
  { id: "techniciens", label: "Techniciens & équipes", icon: CalendarDays },
  { id: "noise", label: "Filtre anti-bruit", icon: Filter },
  { id: "statuts", label: "Statuts des onglets", icon: Tags },
  { id: "pieces", label: "Pièces requises", icon: Package },
  { id: "pose", label: "Dossier de pose", icon: FileStack },
  { id: "messages", label: "Modèles de messages", icon: MessageSquare },
  { id: "automations", label: "Automatisations", icon: Zap },
  { id: "learning", label: "Apprentissage IA", icon: Sparkles },
];

export default function ConfigurationPage() {
  const [tab, setTab] = useState(() => { const t = localStorage.getItem("norea_config_tab"); return t && !["partners", "financial", "templates"].includes(t) ? t : "entreprise"; });

  const setTabAndStore = (t) => {
    setTab(t);
    localStorage.setItem("norea_config_tab", t);
  };

  return (
    <AppLayout>
      <PageHeader
        title="Configuration"
        subtitle="Sources de vérité métier — modifiables sans redéploiement."
        breadcrumb={[{ label: "Norea", to: "/" }, "Configuration"]}
        actions={
          <div className="flex items-center gap-2">
            <a href="/configuration/listes" className="btn-secondary text-xs" data-testid="link-settings-lists">
              Listes de référence
            </a>
            <a href="/configuration/schemas" className="btn-secondary text-xs" data-testid="link-settings-schemas">
              Schémas d'onglets
            </a>
          </div>
        }
      />

      {/* Tabs */}
      <div className="mb-6 inline-flex items-center gap-0.5 bg-white border border-norea-border rounded-md p-0.5" data-testid="config-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTabAndStore(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition ${
                active ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"
              }`}
              data-testid={`config-tab-${t.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "entreprise" && <EntreprisePanel />}
      {tab === "familles" && <FamiliesPanel />}
      {tab === "financial" && (
        <SimpleListPanel
          key="financial"
          listKey="partenaires-financiers"
          title="Partenaires financiers"
          helper="Organismes de financement proposés dans le champ « Partenaire financier » du dossier. Il est pré-rempli automatiquement quand l'organisme lu sur le bon de commande correspond à un nom de cette liste."
          placeholder="Ex. Sofinco"
          emptyLabel="Aucun partenaire financier — ajoutez-en avec le bouton ci-dessus."
          testid="financial-partners"
        />
      )}
      {tab === "subcontractors" && <SubcontractorsPanel />}
      {tab === "techniciens" && <TechniciensPanel />}
      {tab === "noise" && (
        <SimpleListPanel
          key="noise"
          listKey="filtre-anti-bruit"
          title="Filtre anti-bruit"
          helper="Les emails dont l'expéditeur (adresse ou nom) contient l'un de ces mots sont masqués de la Boîte Réponses. Ils restent visibles dans la timeline des dossiers. Ex. « yousign », « scantomail », « nepasrepondre »."
          placeholder="Ex. yousign"
          emptyLabel="Aucun filtre — tous les emails entrants apparaissent dans la Boîte Réponses."
          testid="noise-filter"
        />
      )}
      {tab === "statuts" && <TabStatusesPanel />}
      {tab === "pieces" && <PiecesPanel />}
      {tab === "pose" && <DossierPoseOrderPanel />}
      {tab === "messages" && <MessageTemplatesPanel />}
      {tab === "automations" && <AutomationsPanel />}
      {tab === "learning" && <LearningPanel />}
    </AppLayout>
  );
}

// ============== PARTNERS ==============
function PartnersPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/configuration/partners");
      setItems(data.partners || []);
      setDirty(false);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const update = (i, field, val) => {
    const copy = items.slice();
    copy[i] = { ...copy[i], [field]: val };
    setItems(copy);
    setDirty(true);
  };
  const remove = (i) => {
    setItems(items.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  const add = () => {
    setItems([...items, { email: "", name: "", whatsapp: "", internal: false }]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/configuration/partners", { partners: items });
      toast.success("Régies commerciales enregistrées");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Restaurer la liste par défaut ? Vos modifications seront perdues.")) return;
    setSaving(true);
    try {
      await api.post("/configuration/partners/reset");
      toast.success("Liste réinitialisée");
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton />;

  return (
    <div className="card-flat p-6" data-testid="panel-partners">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-xl font-medium text-norea-text">Régies commerciales</h3>
          <p className="text-xs text-norea-textMuted mt-1 max-w-xl">
            Seuls les emails provenant de ces adresses seront traités par l'agent. Les autres seront ignorés silencieusement.
            Le nom de la régie est reporté automatiquement dans le champ « Régie commerciale » du dossier.
            Cochez « Interne » pour les adresses Norea : la vraie régie est alors retrouvée dans l'email transféré.
          </p>
        </div>
        <button onClick={add} className="btn-secondary" data-testid="btn-add-partner">
          <Plus className="w-4 h-4" />
          Ajouter
        </button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-sm text-norea-textMuted italic py-6 text-center border border-dashed border-norea-border rounded">
            Aucune régie commerciale — ajoutez-en avec le bouton ci-dessus.
          </div>
        )}
        {items.length > 0 && (
          <div className="grid grid-cols-[1.4fr_1fr_1fr_auto_auto] gap-3 items-center px-1 pb-1 text-[10px] uppercase tracking-wider font-semibold text-norea-textMuted">
            <div>Email</div>
            <div>Nom de la régie</div>
            <div>Numéro WhatsApp</div>
            <div title="Adresse Norea : la vraie régie est recherchée dans l'email transféré">Interne</div>
            <div></div>
          </div>
        )}
        {items.map((p, i) => (
          <div key={i} className="grid grid-cols-[1.4fr_1fr_1fr_auto_auto] gap-3 items-center group" data-testid={`partner-row-${i}`}>
            <input
              type="email"
              value={p.email}
              onChange={(e) => update(i, "email", e.target.value)}
              placeholder="email@partenaire.fr"
              className="input-field font-mono text-xs"
              data-testid={`partner-email-${i}`}
            />
            <input
              type="text"
              value={p.name}
              onChange={(e) => update(i, "name", e.target.value)}
              placeholder="Nom de la régie"
              className="input-field text-sm"
              data-testid={`partner-name-${i}`}
            />
            <input
              type="tel"
              value={p.whatsapp || ""}
              onChange={(e) => update(i, "whatsapp", e.target.value)}
              placeholder="+33 6 12 34 56 78"
              className="input-field font-mono text-xs"
              title="Numéro WhatsApp utilisé pour les relances en 1 clic. Accepte 0612... ou +33612..."
              data-testid={`partner-whatsapp-${i}`}
            />
            <label className="flex items-center justify-center w-12" title="Adresse interne Norea : la vraie régie est recherchée dans l'email transféré">
              <input
                type="checkbox"
                checked={!!p.internal}
                onChange={(e) => update(i, "internal", e.target.checked)}
                className="w-4 h-4 accent-norea-primary"
                data-testid={`partner-internal-${i}`}
              />
            </label>
            <button
              onClick={() => remove(i)}
              className="p-2 text-norea-textMuted hover:text-status-erreurText transition"
              data-testid={`partner-remove-${i}`}
              aria-label="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <ActionBar
        dirty={dirty}
        saving={saving}
        onSave={save}
        onReset={reset}
        resetLabel="Restaurer défauts"
        testid="partners"
      />
    </div>
  );
}

// ============== SIMPLE LISTS (partenaires financiers, filtre anti-bruit) ==============
function SimpleListPanel({ listKey, title, helper, placeholder, emptyLabel, testid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/configuration/lists/${listKey}`);
      setItems(data.items || []);
      setDirty(false);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey]);

  const change = (next) => {
    setItems(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/configuration/lists/${listKey}`, { items: items.filter((i) => i.name.trim()) });
      toast.success(`${title} enregistré(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton />;

  return (
    <div className="card-flat p-6" data-testid={`panel-${testid}`}>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-xl font-medium text-norea-text">{title}</h3>
          <p className="text-xs text-norea-textMuted mt-1 max-w-xl">{helper}</p>
        </div>
        <button onClick={() => change([...items, { name: "", meta: {} }])} className="btn-secondary" data-testid={`btn-add-${testid}`}>
          <Plus className="w-4 h-4" />
          Ajouter
        </button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-sm text-norea-textMuted italic py-6 text-center border border-dashed border-norea-border rounded">
            {emptyLabel}
          </div>
        )}
        {items.map((p, i) => (
          <div key={p.id || `new-${i}`} className="flex items-center gap-3" data-testid={`${testid}-row-${i}`}>
            <input
              type="text"
              value={p.name}
              onChange={(e) => change(items.map((it, idx) => (idx === i ? { ...it, name: e.target.value } : it)))}
              placeholder={placeholder}
              className="input-field text-sm flex-1"
              data-testid={`${testid}-name-${i}`}
            />
            <button
              onClick={() => change(items.filter((_, idx) => idx !== i))}
              className="p-2 text-norea-textMuted hover:text-status-erreurText transition"
              data-testid={`${testid}-remove-${i}`}
              aria-label="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <ActionBar dirty={dirty} saving={saving} onSave={save} onReset={load} resetLabel="Annuler les modifications" testid={testid} />
    </div>
  );
}

// ============== PIECES REQUISES ==============
function PiecesPanel() {
  const [data, setData] = useState(null);
  const [produits, setProduits] = useState([]);
  const [currentProd, setCurrentProd] = useState("PV");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get("/configuration/pieces");
      setData(r.pieces || {});
      setProduits(r.produits || []);
      setDirty(false);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const updatePiece = (idx, val) => {
    const copy = { ...data };
    const arr = (copy[currentProd] || []).slice();
    arr[idx] = val;
    copy[currentProd] = arr;
    setData(copy);
    setDirty(true);
  };
  const removePiece = (idx) => {
    const copy = { ...data };
    copy[currentProd] = (copy[currentProd] || []).filter((_, i) => i !== idx);
    setData(copy);
    setDirty(true);
  };
  const addPiece = () => {
    const copy = { ...data };
    copy[currentProd] = [...(copy[currentProd] || []), ""];
    setData(copy);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/configuration/pieces", { pieces: data });
      toast.success("Pièces enregistrées");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Restaurer la configuration par défaut pour tous les produits ?")) return;
    setSaving(true);
    try {
      await api.post("/configuration/pieces/reset");
      toast.success("Pièces réinitialisées");
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) return <Skeleton />;

  const pieces = data[currentProd] || [];

  return (
    <div className="card-flat p-6" data-testid="panel-pieces">
      <div className="mb-4">
        <h3 className="font-heading text-xl font-medium text-norea-text">Pièces requises par produit</h3>
        <p className="text-xs text-norea-textMuted mt-1 max-w-xl">
          Définissez la liste des pièces qu'un dossier doit contenir pour être considéré <strong>COMPLET</strong>.
        </p>
      </div>

      {/* Produit selector */}
      <div className="mb-4 inline-flex items-center gap-0.5 bg-norea-bg border border-norea-border rounded-md p-0.5">
        {produits.map((prod) => {
          const active = currentProd === prod;
          const count = (data[prod] || []).length;
          return (
            <button
              key={prod}
              onClick={() => setCurrentProd(prod)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition ${
                active ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"
              }`}
              data-testid={`pieces-produit-${prod}`}
            >
              {prod}
              <span className={`text-[10px] font-mono ${active ? "text-white/80" : "text-norea-textDisabled"}`}>
                {count}
              </span>
            </button>
          );
        })}
        <button onClick={addPiece} className="ml-2 btn-secondary !py-1 !px-2 text-xs" data-testid="btn-add-piece">
          <Plus className="w-3.5 h-3.5" />
          Pièce
        </button>
      </div>

      <div className="space-y-2">
        {pieces.length === 0 && (
          <div className="text-sm text-norea-textMuted italic py-6 text-center border border-dashed border-norea-border rounded">
            Aucune pièce pour ce produit — ajoutez-en au moins une.
          </div>
        )}
        {pieces.map((p, i) => (
          <div key={i} className="flex items-center gap-2 group" data-testid={`piece-row-${i}`}>
            <span className="text-[10px] text-norea-textDisabled font-mono w-6 text-right">{i + 1}.</span>
            <input
              type="text"
              value={p}
              onChange={(e) => updatePiece(i, e.target.value)}
              placeholder="Nom de la pièce (ex: CNI recto-verso)"
              className="input-field text-sm flex-1"
              data-testid={`piece-input-${i}`}
            />
            <button
              onClick={() => removePiece(i)}
              className="p-2 text-norea-textMuted hover:text-status-erreurText transition"
              data-testid={`piece-remove-${i}`}
              aria-label="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <ActionBar
        dirty={dirty}
        saving={saving}
        onSave={save}
        onReset={reset}
        resetLabel="Restaurer défauts (tous produits)"
        testid="pieces"
      />
    </div>
  );
}

// ============== TEMPLATES WHATSAPP ==============
const KNOWN_VARIABLES = [
  "prenom_client",
  "nom_client",
  "produit",
  "organisme",
  "prix_vente",
  "date_signature",
  "pieces_manquantes",
  "partenaire",
  "nb_relances",
];

function TemplatesPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // { id, label, tone, body, order }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/configuration/templates");
      setItems(data.templates || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const saveTemplate = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const isNew = !items.some((t) => t.id === editing.id);
      if (isNew) {
        await api.post("/configuration/templates", editing);
      } else {
        const { id, ...rest } = editing;
        await api.put(`/configuration/templates/${id}`, rest);
      }
      toast.success("Template enregistré");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const removeTemplate = async (id) => {
    if (!window.confirm(`Supprimer le template "${id}" ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/configuration/templates/${id}`);
      toast.success("Template supprimé");
      load();
    } finally {
      setBusy(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Réinitialiser tous les templates ? Toutes vos modifications seront perdues.")) return;
    setBusy(true);
    try {
      await api.post("/configuration/templates/reset");
      toast.success("Templates réinitialisés");
      load();
    } finally {
      setBusy(false);
    }
  };

  const addNew = () => {
    setEditing({
      id: `custom-${Date.now()}`,
      label: "Nouveau template",
      tone: "neutre",
      body: "Bonjour,\n\n…\n\nBonne journée,\nL'équipe Norea",
      order: 99,
    });
  };

  if (loading) return <Skeleton />;

  return (
    <div className="space-y-4" data-testid="panel-templates">
      <div className="card-flat p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-xl font-medium text-norea-text">Templates de relance</h3>
            <p className="text-xs text-norea-textMuted mt-1 max-w-xl">
              Les messages prêts à copier-coller dans WhatsApp depuis la page Détail d'un dossier. Variables disponibles :{" "}
              {KNOWN_VARIABLES.map((v) => (
                <code key={v} className="font-mono text-[10px] bg-norea-bg border border-norea-border px-1 py-0.5 rounded mr-1">
                  {`{${v}}`}
                </code>
              ))}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={resetAll} disabled={busy} className="btn-ghost text-xs" data-testid="btn-templates-reset">
              <RotateCcw className="w-3.5 h-3.5" />
              Réinitialiser
            </button>
            <button onClick={addNew} className="btn-secondary" data-testid="btn-templates-add">
              <Plus className="w-4 h-4" />
              Nouveau
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {items.length === 0 && (
            <div className="text-sm text-norea-textMuted italic py-6 text-center border border-dashed border-norea-border rounded">
              Aucun template — créez-en un.
            </div>
          )}
          {items.map((t) => (
            <div
              key={t.id}
              className="border border-norea-border rounded-md p-3 hover:border-norea-primary/40 transition"
              data-testid={`template-card-${t.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-norea-text">{t.label}</span>
                    <span className="text-[10px] uppercase tracking-wider font-semibold bg-norea-surfaceHover text-norea-textMuted border border-norea-border rounded px-1.5 py-0.5">
                      {t.tone}
                    </span>
                    <span className="text-[10px] font-mono text-norea-textDisabled">#{t.order}</span>
                  </div>
                  <pre className="mt-2 text-xs text-norea-textMuted whitespace-pre-wrap font-sans line-clamp-3 max-h-16 overflow-hidden">
                    {t.body}
                  </pre>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setEditing({ ...t })}
                    className="btn-ghost text-xs"
                    data-testid={`template-edit-${t.id}`}
                  >
                    Éditer
                  </button>
                  <button
                    onClick={() => removeTemplate(t.id)}
                    className="p-1.5 text-norea-textMuted hover:text-status-erreurText"
                    data-testid={`template-delete-${t.id}`}
                    aria-label="Supprimer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <TemplateEditorModal
          template={editing}
          busy={busy}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={saveTemplate}
        />
      )}
    </div>
  );
}

function TemplateEditorModal({ template, busy, onChange, onCancel, onSave }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={busy ? undefined : onCancel}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl border border-norea-border max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="template-editor-modal"
      >
        <div className="px-6 py-4 border-b border-norea-border flex items-start justify-between">
          <div>
            <h3 className="font-heading text-xl font-medium text-norea-text">Édition du template</h3>
            <p className="text-xs text-norea-textMuted mt-0.5">ID : <code className="font-mono">{template.id}</code></p>
          </div>
          <button onClick={onCancel} disabled={busy} className="text-norea-textMuted hover:text-norea-text">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="overline block mb-1.5">Libellé</label>
              <input
                type="text"
                value={template.label || ""}
                onChange={(e) => onChange({ ...template, label: e.target.value })}
                className="input-field text-sm"
                data-testid="template-label-input"
              />
            </div>
            <div>
              <label className="overline block mb-1.5">Ton</label>
              <select
                value={template.tone || "neutre"}
                onChange={(e) => onChange({ ...template, tone: e.target.value })}
                className="input-field text-sm"
                data-testid="template-tone-select"
              >
                {["neutre", "ferme", "urgent", "positif"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="overline block mb-1.5">Corps du message</label>
            <textarea
              value={template.body || ""}
              onChange={(e) => onChange({ ...template, body: e.target.value })}
              className="input-field text-sm font-mono"
              rows={14}
              placeholder="Bonjour, …"
              data-testid="template-body-input"
            />
            <div className="mt-2 text-[11px] text-norea-textMuted">
              Variables disponibles :{" "}
              {KNOWN_VARIABLES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => onChange({ ...template, body: (template.body || "") + `{${v}}` })}
                  className="font-mono text-[10px] bg-norea-bg border border-norea-border px-1 py-0.5 rounded mr-1 hover:bg-norea-surfaceHover"
                  data-testid={`template-var-${v}`}
                >
                  {`{${v}}`}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-norea-bg border-t border-norea-border flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="btn-secondary" data-testid="template-editor-cancel">
            Annuler
          </button>
          <button onClick={onSave} disabled={busy} className="btn-primary" data-testid="template-editor-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// ============== SHARED ==============
function Skeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-12 bg-white border border-norea-border rounded-md animate-pulse" />
      ))}
    </div>
  );
}

function ActionBar({ dirty, saving, onSave, onReset, resetLabel, testid }) {
  return (
    <div className="mt-6 pt-4 border-t border-norea-border flex items-center justify-between gap-2">
      <button onClick={onReset} disabled={saving} className="btn-ghost text-xs" data-testid={`btn-${testid}-reset`}>
        <RotateCcw className="w-3.5 h-3.5" />
        {resetLabel}
      </button>
      <div className="flex items-center gap-3">
        {dirty && (
          <span className="text-[11px] text-status-incompletText">● Modifications non enregistrées</span>
        )}
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          className="btn-primary"
          data-testid={`btn-${testid}-save`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}


// ============== LEARNING (AI category suggestions from recurring titres_doc) ==============
function LearningPanel() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minCount, setMinCount] = useState(3);
  const [pendingPromotion, setPendingPromotion] = useState(null); // {slug, suggested_name}
  const [newLabel, setNewLabel] = useState("");
  const [promoting, setPromoting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dossiers/_meta/learning-suggestions`, { params: { min_count: minCount } });
      setSuggestions(data.suggestions || []);
    } catch {
      toast.error("Impossible de charger les suggestions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [minCount]);

  const openPromote = (s) => {
    setPendingPromotion(s);
    setNewLabel(s.suggested_name);
  };

  const confirmPromote = async () => {
    if (!pendingPromotion || !newLabel.trim()) return;
    setPromoting(true);
    try {
      const { data } = await api.post(`/dossiers/_meta/promote-titre-doc`, {
        slug: pendingPromotion.slug,
        new_type_piece: newLabel.trim(),
        keep_titre_doc: true,
      });
      toast.success(
        `✓ "${data.new_type_piece}" créée — ${data.updated_attachments} pièce(s) reclassée(s) sur ${data.updated_dossiers} dossier(s)`,
        { duration: 4500 }
      );
      setPendingPromotion(null);
      setNewLabel("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la promotion");
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="panel-learning">
      <div className="card-flat p-5">
        <div className="flex items-start gap-3 mb-2">
          <div className="w-9 h-9 rounded-md bg-norea-primary/10 flex items-center justify-center shrink-0">
            <Lightbulb className="w-5 h-5 text-norea-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-base font-medium text-norea-text">L'IA apprend de tes documents</h3>
            <p className="text-xs text-norea-textMuted mt-1 leading-relaxed">
              Quand l'IA détecte un document qu'elle ne peut pas classer dans une catégorie connue, elle le marque « Autre »
              et capture le titre lu en haut de page. Si un même titre revient sur plusieurs dossiers, c'est probablement une
              catégorie officielle manquante — promeus-la d'un clic et tous les anciens dossiers seront reclassés
              rétroactivement.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-3 pl-12">
          <label className="text-xs text-norea-textMuted">Minimum de dossiers concernés :</label>
          <input
            type="number"
            min={2}
            max={20}
            value={minCount}
            onChange={(e) => setMinCount(Math.max(2, Math.min(20, parseInt(e.target.value || "3", 10))))}
            className="input-field text-xs w-16 text-center tabular-nums"
            data-testid="learning-min-count"
          />
          <button onClick={load} className="btn-ghost text-xs" data-testid="learning-refresh">
            <RotateCcw className="w-3 h-3" />
            Rafraîchir
          </button>
        </div>
      </div>

      {loading && (
        <div className="card-flat p-8 text-center">
          <Loader2 className="w-5 h-5 text-norea-primary animate-spin mx-auto" />
        </div>
      )}

      {!loading && suggestions.length === 0 && (
        <div className="card-flat p-8 text-center" data-testid="learning-empty">
          <div className="w-12 h-12 rounded-full bg-norea-bg mx-auto mb-3 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-norea-textMuted" />
          </div>
          <div className="text-sm font-medium text-norea-text">Aucune catégorie récurrente détectée</div>
          <div className="text-xs text-norea-textMuted mt-1 max-w-md mx-auto">
            Au fil du traitement, l'IA va remonter ici les types de documents qu'elle voit revenir plusieurs fois en
            « Autre ». Tu pourras les promouvoir en catégorie officielle.
          </div>
        </div>
      )}

      {!loading && suggestions.length > 0 && (
        <div className="space-y-3" data-testid="learning-suggestions">
          {suggestions.map((s) => (
            <div
              key={s.slug}
              className="card-flat p-4"
              data-testid={`suggestion-${s.slug}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-norea-primary/10 text-norea-primary border border-norea-primary/30 rounded">
                      Suggestion
                    </span>
                    <span className="font-heading text-base font-medium text-norea-text truncate">
                      « {s.suggested_name} »
                    </span>
                  </div>
                  <div className="text-xs text-norea-textMuted">
                    Détecté sur <strong className="text-norea-text tabular-nums">{s.distinct_dossiers}</strong> dossier(s)
                    {" "}({s.total_occurrences} occurrence{s.total_occurrences > 1 ? "s" : ""})
                    {" "}— actuellement classé en « Autre »
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.examples.slice(0, 4).map((ex) => (
                      <RouterLink
                        key={ex.dossier_id}
                        to={`/dossiers/${ex.dossier_id}`}
                        className="inline-flex items-center gap-1 text-[10px] bg-norea-bg hover:bg-norea-surfaceHover border border-norea-border text-norea-textMuted hover:text-norea-text rounded px-2 py-0.5 transition"
                        title={`Voir ${ex.client}`}
                      >
                        {ex.client}
                        <ChevronRight className="w-2.5 h-2.5" />
                      </RouterLink>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => openPromote(s)}
                  className="btn-primary text-xs shrink-0"
                  data-testid={`promote-${s.slug}`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Promouvoir en catégorie
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Promotion modal */}
      {pendingPromotion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-norea-text/40 backdrop-blur-sm" onClick={() => !promoting && setPendingPromotion(null)}>
          <div className="bg-white rounded-lg shadow-2xl border border-norea-border max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()} data-testid="promote-modal">
            <h3 className="font-heading text-lg font-medium text-norea-text mb-1">Créer une nouvelle catégorie</h3>
            <p className="text-xs text-norea-textMuted mb-4">
              Quel nom officiel veux-tu donner à ce type de document ?
              Les {pendingPromotion.distinct_dossiers} dossiers concernés seront automatiquement reclassés.
            </p>
            <label className="text-xs font-medium text-norea-text">Nom de la catégorie</label>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              autoFocus
              maxLength={80}
              className="input-field text-sm w-full mt-1"
              data-testid="promote-label-input"
              onKeyDown={(e) => { if (e.key === "Enter") confirmPromote(); }}
            />
            <div className="text-[10px] text-norea-textMuted mt-1">
              Conseil : utilise un nom court et précis (ex: « Attestation TVA », « Plan de masse »).
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setPendingPromotion(null)} disabled={promoting} className="btn-secondary text-sm">
                Annuler
              </button>
              <button
                onClick={confirmPromote}
                disabled={promoting || !newLabel.trim()}
                className="btn-primary text-sm"
                data-testid="promote-confirm"
              >
                {promoting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Créer & reclasser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
